import {
  cancelledByBuyer,
  centsToUnits,
  purchaseOrderDocumentProblem,
  PURCHASE_ORDER_DOCUMENT_EXTENSIONS,
  type PurchaseOrder,
  type PurchaseOrderParty,
} from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Download, FileText, Pencil, Upload } from "lucide-react";
import { useRef, useState, type JSX, type ReactNode } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { useAuth } from "../../../auth/AuthProvider.js";
import { BackButton } from "../../../components/ui/back-button.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { ApiError } from "../../../lib/api.js";
import { date, dateOnly, money, text } from "../../../lib/format.js";
import {
  cancelPurchaseOrder,
  documentIsPdf,
  downloadPurchaseOrderDocument,
  downloadPurchaseOrderPdf,
  fetchPurchaseOrderDocument,
  fetchPurchaseOrderPdf,
  getPurchaseOrder,
  purchaseOrderKeys,
  purchaseOrderPdfFilename,
  updatePurchaseOrder,
  uploadPurchaseOrderDocument,
} from "../../../lib/purchaseOrdersApi.js";
import { PdfInlinePreview } from "../../knowledge-base/PdfInlinePreview.js";
import { PageHeader } from "../crm.js";
import { PurchaseOrderSourceChip, PurchaseOrderStatusChip, purchaseOrderTitle } from "./purchase-order-ui.js";

/**
 * One purchase order (2026-09-17).
 *
 * WHAT THE RESELLER MAY DO is crm-api's verdict, `editable`, and nothing else:
 * while XOP has not yet acted on the order (status `received`) the reseller
 * can correct its PO number and note and attach or replace its signed copy;
 * once XOP accepts or cancels it the page is a record. The rule is the
 * server's — this page never compares `status` itself — and `invoices:edit`
 * on top is the usual rendering courtesy.
 *
 * CANCELLING IS THE RESELLER'S ONLY WAY OUT, and it is a second verdict
 * (`cancellable`), not a reading of the first (CEO, 2026-09-17). Staff delete
 * a purchase order; a reseller withdraws one, which leaves the record — and
 * XOP's copy of it — standing. There is deliberately NO delete control on
 * this page and there must never be one.
 *
 * THE DOCUMENT. A PO made from a quote has lines, so crm-api renders the
 * standard PO PDF (`has_pdf`) and that is what is previewed. An uploaded PO
 * has the buyer's own file instead; it is previewed when it is a PDF and
 * offered for download whatever it is. Both render through pdf.js, never an
 * iframe, with the bytes fetched through this origin.
 */

export function PurchaseOrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const poId = Number(id);
  const askable = Number.isInteger(poId) && poId > 0;

  const { data: po, isPending, error } = useQuery({
    queryKey: purchaseOrderKeys.detail(poId),
    queryFn: () => getPurchaseOrder(poId),
    enabled: askable,
  });

  if (!askable) return <MissingPanel />;
  if (error instanceof ApiError && error.status === 403) return <NoAccessPanel permission="invoices:view" />;
  if (isPending) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress size={26} aria-label="Loading" />
      </Box>
    );
  }
  if (error || !po) {
    if (!error || (error instanceof ApiError && error.status === 404)) return <MissingPanel />;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <BackToList />
        <Alert severity="error" data-testid="po-load-error">
          {error.message || "That purchase order could not be loaded."}
        </Alert>
      </Box>
    );
  }

  return <PurchaseOrderDocument po={po} />;
}

function PurchaseOrderDocument({ po }: { po: PurchaseOrder }): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canChange = po.editable && can("invoices:edit");
  // `cancellable` already carries crm-api's own permission check; `can` on top
  // is the same rendering courtesy `canChange` pays, so a viewer is never
  // shown a button the server would refuse.
  const canCancel = po.cancellable && can("invoices:edit");

  const [editing, setEditing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [poNumber, setPoNumber] = useState(po.poNumber ?? "");
  const [note, setNote] = useState(po.note ?? "");
  const [notice, setNotice] = useState<{ severity: "error" | "success"; text: string } | null>(null);
  const [busy, setBusy] = useState<"pdf" | "document" | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const store = (next: PurchaseOrder): void => {
    // The answer IS the new record, so the page takes it as-is; only the
    // lists (whose rows show the PO number) need asking again.
    queryClient.setQueryData(purchaseOrderKeys.detail(po.id), next);
    void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.lists });
  };

  const save = useMutation({
    mutationFn: () =>
      updatePurchaseOrder(po.id, {
        poNumber: poNumber.trim() === "" ? null : poNumber.trim(),
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: (next) => {
      store(next);
      setEditing(false);
      setNotice({ severity: "success", text: "Saved." });
    },
    onError: (err) => setNotice({ severity: "error", text: err.message }),
  });

  /**
   * The order is withdrawn upstream and the answer IS the cancelled record,
   * so the page re-renders off `cancellable` / `editable` going false rather
   * than off anything this component decides.
   *
   * A refusal (crm-api's 409 when XOP has already acted, or when the record
   * is XOP's own upload) is shown VERBATIM: it says something this page did
   * not know a moment ago, and paraphrasing it would only blur that.
   */
  const cancelOrder = useMutation({
    mutationFn: () => cancelPurchaseOrder(po.id),
    onSuccess: (next) => {
      store(next);
      setEditing(false);
      setNotice({ severity: "success", text: "This purchase order is cancelled. XOP Networks has been told." });
    },
    onError: (err) => {
      setNotice({ severity: "error", text: err.message });
      // The refusal means the order is not what this page is showing — it was
      // decided elsewhere while the page was open — so ask again rather than
      // leave a Cancel button over a stale row.
      void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(po.id) });
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadPurchaseOrderDocument(po.id, file),
    onSuccess: (next) => {
      store(next);
      setNotice({ severity: "success", text: "Your document is attached." });
    },
    onError: (err) => setNotice({ severity: "error", text: err.message }),
  });

  const download = async (kind: "pdf" | "document"): Promise<void> => {
    setBusy(kind);
    setNotice(null);
    try {
      await (kind === "pdf" ? downloadPurchaseOrderPdf(po) : downloadPurchaseOrderDocument(po));
    } catch (err) {
      setNotice({ severity: "error", text: err instanceof Error ? err.message : "Download failed." });
    } finally {
      setBusy(null);
    }
  };

  const pickFile = (file: File | undefined): void => {
    if (!file) return;
    const problem = purchaseOrderDocumentProblem(file);
    if (problem) {
      setNotice({ severity: "error", text: problem });
      return;
    }
    upload.mutate(file);
  };

  const description = [
    po.poNumber ? `Your PO # ${po.poNumber}` : null,
    po.orderDate ? `Ordered ${dateOnly(po.orderDate)}` : null,
    po.submittedByEmail ? `sent by ${po.submittedByEmail}` : null,
    po.createdAt ? `on ${date(po.createdAt)}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  const previewPdf = po.hasPdf;
  const previewDocument = !po.hasPdf && po.hasDocument && documentIsPdf(po);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <BackToList />

      <PageHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <span>Purchase order {purchaseOrderTitle(po)}</span>
            <PurchaseOrderStatusChip status={po.status} />
            <PurchaseOrderSourceChip source={po.source} />
          </Box>
        }
        description={description || null}
        actions={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {po.hasPdf && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Download size={16} />}
                disabled={busy === "pdf"}
                onClick={() => void download("pdf")}
              >
                Download PDF
              </Button>
            )}
            {po.hasDocument && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FileText size={16} />}
                disabled={busy === "document"}
                onClick={() => void download("document")}
              >
                Download document
              </Button>
            )}
            {canChange && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  hidden
                  data-testid="po-attach-input"
                  accept={[...PURCHASE_ORDER_DOCUMENT_EXTENSIONS, "application/pdf", "image/*"].join(",")}
                  onChange={(e) => {
                    pickFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Upload size={16} />}
                  disabled={upload.isPending}
                  onClick={() => fileInput.current?.click()}
                >
                  {upload.isPending
                    ? "Uploading…"
                    : po.hasDocument
                      ? "Replace your signed PO"
                      : "Attach your signed PO"}
                </Button>
              </>
            )}
            {canCancel && (
              <Button
                variant="outlined"
                color="error"
                data-testid="po-cancel"
                startIcon={<Ban size={16} />}
                disabled={cancelOrder.isPending}
                onClick={() => setConfirmingCancel(true)}
              >
                Cancel order
              </Button>
            )}
          </Box>
        }
      />

      <ConfirmDialog
        isOpen={confirmingCancel}
        onClose={() => setConfirmingCancel(false)}
        title={`Cancel purchase order ${purchaseOrderTitle(po)}?`}
        description="XOP Networks will be told you cancelled it. This cannot be undone."
        variant="destructive"
        confirmLabel="Cancel order"
        cancelLabel="Keep order"
        onConfirm={async () => {
          // A refusal here is terminal — XOP has already acted on the order —
          // so the dialog CLOSES and the page shows crm-api's own sentence.
          // Keeping it open (the `ConfirmDialog` convention for a retryable
          // failure) would hide that Alert behind the backdrop and invite a
          // retry that cannot succeed.
          try {
            await cancelOrder.mutateAsync();
          } catch {
            /* `onError` has already put crm-api's sentence on the page. */
          }
        }}
      />

      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} data-testid="po-notice">
          {notice.text}
        </Alert>
      )}

      {!po.editable && po.status !== "received" && (
        <Typography data-testid="po-locked" sx={{ fontSize: 13, color: "text.secondary", mt: -1.5 }}>
          {lockedSentence(po)}
        </Typography>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" } }}>
        <PartyCard title="Buyer" party={po.buyer} fallback="Your company" />
        <PartyCard title="Ship to" party={po.shipTo} fallback="—" />
        <FactCard title="Customer">
          {po.customer ? (
            <Link component={RouterLink} to={`/customers/${po.customer.id}`} underline="hover">
              {text(po.customer.companyName)}
            </Link>
          ) : (
            "—"
          )}
        </FactCard>
        <FactCard title="Quote">
          {po.resellerQuote ? (
            <Link
              component={RouterLink}
              to={`/quotes/${po.resellerQuote.id}`}
              underline="hover"
              data-testid="po-quote-link"
            >
              {po.resellerQuote.number ?? `#${po.resellerQuote.id}`}
            </Link>
          ) : (
            "—"
          )}
        </FactCard>
      </Box>

      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>
              Your PO number and note
            </Typography>
            {canChange && !editing && (
              <Button size="small" startIcon={<Pencil size={14} />} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </Box>
          {editing ? (
            <>
              <TextField
                size="small"
                label="Your PO number"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                inputProps={{ maxLength: 100, "aria-label": "Your PO number" }}
                sx={{ maxWidth: 360 }}
              />
              <TextField
                multiline
                minRows={3}
                size="small"
                label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 2000, "aria-label": "Note" }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button
                  color="inherit"
                  disabled={save.isPending}
                  onClick={() => {
                    setEditing(false);
                    setPoNumber(po.poNumber ?? "");
                    setNote(po.note ?? "");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography sx={{ fontSize: 14 }} data-testid="po-number-value">
                <Box component="span" sx={{ color: "text.secondary" }}>PO number: </Box>
                {text(po.poNumber)}
              </Typography>
              <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }} data-testid="po-note-value">
                <Box component="span" sx={{ color: "text.secondary" }}>Note: </Box>
                {text(po.note)}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <LinesCard po={po} />

      {(previewPdf || previewDocument) && (
        <Card>
          <CardContent>
            <PdfInlinePreview
              docId={po.id}
              filename={previewPdf ? purchaseOrderPdfFilename(po) : (po.document?.filename ?? "document.pdf")}
              cacheKey={
                previewPdf
                  ? purchaseOrderKeys.pdf(po.id, po.updatedAt)
                  : purchaseOrderKeys.document(po.id, po.updatedAt)
              }
              loader={() => (previewPdf ? fetchPurchaseOrderPdf(po.id) : fetchPurchaseOrderDocument(po.id))}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

/**
 * Why this order can no longer be changed.
 *
 * WHO withdrew it changes what the sentence MEANS — "you cancelled this" and
 * "XOP Networks cancelled this" are opposite pieces of news — so the
 * reseller's own act is never reported as XOP's, and an unrecognised
 * canceller reads as XOP rather than naming an XOP person in this portal (see
 * `cancelledByBuyer`). A cancellation crm-api did not stamp still gets a
 * sentence, without a date it does not have.
 */
function lockedSentence(po: PurchaseOrder): string {
  if (po.status === "cancelled" && cancelledByBuyer(po)) {
    return po.cancelledAt
      ? `You cancelled this on ${date(po.cancelledAt)}.`
      : "You cancelled this purchase order.";
  }
  return `XOP Networks has ${po.status === "accepted" ? "accepted" : "cancelled"} this purchase order, so it can no longer be changed.`;
}

function LinesCard({ po }: { po: PurchaseOrder }): JSX.Element {
  const amount = (cents: number | null): string => money(centsToUnits(cents), po.currency);
  return (
    <Card>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>
          Items
        </Typography>
        {po.lines.length === 0 ? (
          <Typography data-testid="po-no-lines" sx={{ fontSize: 13.5, color: "text.secondary" }}>
            The items are on the attached document.
            {po.totalCents !== null ? ` Order total: ${amount(po.totalCents)}.` : ""}
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 560 }} data-testid="po-lines">
              <TableHead>
                <TableRow>
                  <TableCell>Part number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit price</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {po.lines.map((line, index) => (
                  <TableRow key={`${line.partNumber}-${index}`}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{line.partNumber || "—"}</TableCell>
                    <TableCell>{line.description || "—"}</TableCell>
                    <TableCell align="right">
                      {line.quantity}
                      {line.unit ? ` ${line.unit}` : ""}
                    </TableCell>
                    <TableCell align="right">{amount(line.unitPriceCents)}</TableCell>
                    <TableCell align="right">{amount(line.lineTotalCents)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ color: "text.secondary" }}>
                    Subtotal
                  </TableCell>
                  <TableCell align="right">{amount(po.subtotalCents)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }} data-testid="po-total">
                    {amount(po.totalCents)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function FactCard({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Card>
      <CardContent>
        <Typography
          sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary", mb: 0.75 }}
        >
          {title}
        </Typography>
        <Box sx={{ fontSize: 14 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function PartyCard({
  title,
  party,
  fallback,
}: {
  title: string;
  party: PurchaseOrderParty | null;
  fallback: string;
}): JSX.Element {
  const place = party
    ? [party.city, party.state, party.postalCode].filter((p): p is string => p !== null).join(", ")
    : "";
  return (
    <FactCard title={title}>
      {party && party.name ? (
        <Box data-testid={`po-party-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{party.name}</Typography>
          {party.address && <Typography sx={{ fontSize: 13 }}>{party.address}</Typography>}
          {place && <Typography sx={{ fontSize: 13 }}>{place}</Typography>}
          {party.country && <Typography sx={{ fontSize: 13 }}>{party.country}</Typography>}
          {party.email && (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{party.email}</Typography>
          )}
        </Box>
      ) : (
        fallback
      )}
    </FactCard>
  );
}

function BackToList(): JSX.Element {
  return (
    <Box>
      <BackButton to="/purchase-orders" label="Back to purchase orders" />
    </Box>
  );
}

function MissingPanel(): JSX.Element {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <BackToList />
      <Alert severity="info" data-testid="po-not-found">
        That purchase order could not be found. It may belong to another reseller.
      </Alert>
    </Box>
  );
}

export default PurchaseOrderDetailPage;
